import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../../utils/auth';

/**
 * PreSalesOrderCreationModalSF - Salesforce-specific Sales Order Review Modal
 * Handles Salesforce Opportunities/Orders with Products and Pricebook Entries
 */
const PreSalesOrderCreationModalSF = ({
  salesOrderData,
  onConfirm,
  onCancel,
  onClose,
  token,
  workflowContext,
  runId: propRunId,
  workflowId: propWorkflowId,
  agentId = null  // Track source for cancel navigation
}) => {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedProducts, setSelectedProducts] = useState({});
  const [allAccounts, setAllAccounts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [productSearchTerms, setProductSearchTerms] = useState({});
  const [loadingAllAccounts, setLoadingAllAccounts] = useState(false);
  const [loadingAllProducts, setLoadingAllProducts] = useState(false);

  // Creation status
  const [creationStatus, setCreationStatus] = useState(null);
  const [creationMessage, setCreationMessage] = useState('');
  const [salesforceUrl, setSalesforceUrl] = useState('');

  // Minimize state
  const [isMinimized, setIsMinimized] = useState(false);

  // Check if we're still waiting for workflowContext
  const isWaitingForContext = useMemo(() => {
    if (propRunId && !workflowContext) {
      console.log('⏳ [PreSalesOrderCreationModalSF] Waiting for workflowContext to load...');
      return true;
    }
    return false;
  }, [propRunId, workflowContext]);

  // Get current data - extract from workflow context step data (similar to NetSuite pattern)
  const currentData = useMemo(() => {
    console.log('📦 [PreSalesOrderCreationModalSF] salesOrderData:', salesOrderData);
    console.log('📦 [PreSalesOrderCreationModalSF] workflowContext:', workflowContext);

    if (!salesOrderData && !workflowContext) return null;

    // Extract data from workflow context step data
    // Account data from search_salesforce_account_ai_step_data.SelectedAccount or root level
    const accountStepData = workflowContext?.search_salesforce_account_ai_step_data;
    const accountData = accountStepData?.SelectedAccount || accountStepData;

    // PO data from extract_purchase_order_data_ai_step_data
    // Handle both old format (PONumber at root) and new schema format (sale_data.customer_po_number)
    const poStepData = workflowContext?.extract_purchase_order_data_ai_step_data;
    const poData = poStepData?.sale_data || poStepData; // Unwrap sale_data if present

    // Product mapping from product_mapping_ai_step_data
    const mappingData = workflowContext?.product_mapping_ai_step_data;

    console.log('📦 [PreSalesOrderCreationModalSF] accountData:', accountData);
    console.log('📦 [PreSalesOrderCreationModalSF] poData:', poData);
    console.log('📦 [PreSalesOrderCreationModalSF] mappingData:', mappingData);

    // If waiting for context and no data yet, return null
    if (isWaitingForContext && !poData && !mappingData) {
      console.log('⏳ [PreSalesOrderCreationModalSF] No data yet, waiting...');
      return null;
    }

    // Get line items from PO data (handle both PascalCase and snake_case)
    const poLineItems = poData?.LineItems || poData?.line_items || [];
    // Get matched products (has SalesforceProductId, PricebookEntryId)
    const matchedProducts = mappingData?.matched_items || [];

    console.log('📦 [PreSalesOrderCreationModalSF] poLineItems:', poLineItems);
    console.log('📦 [PreSalesOrderCreationModalSF] matchedProducts:', matchedProducts);

    // Merge PO line items with product mapping by line index or SKU
    const mergedItems = poLineItems.map((poItem, index) => {
      // Find matching product by line number or SKU or by index
      const poLineNum = poItem.LineNumber || poItem.line_number;
      const poSku = poItem.ItemCodeSKU || poItem.item_code_sku || poItem.part_number || poItem.PartNumber;

      const matchedProduct = matchedProducts.find(mp => {
        const mpLineNum = mp.po_line_index || mp.LineNumber || mp.line_number;
        const mpSku = mp.ItemCodeSKU || mp.item_code_sku || mp.po_item_description;
        return mpLineNum === poLineNum || mpSku === poSku;
      }) || matchedProducts[index] || {};

      return {
        line_item: {
          LineNumber: poItem.LineNumber || poItem.line_number || null,
          ItemCodeSKU: poItem.ItemCodeSKU || poItem.item_code_sku || poItem.part_number || poItem.PartNumber || '',
          UnitPrice: poItem.UnitPrice || poItem.unit_price || 0,
          Quantity: poItem.Quantity || poItem.quantity || 1,
          TotalAmount: poItem.TotalAmount || poItem.total_amount || poItem.total_price || 0,
          ItemDescription: poItem.ItemDescription || poItem.item_description || poItem.description || poItem.Description || ''
        },
        // Handle various casing conventions: SalesforceProductID, SalesforceProductId, salesforce_product_id
        salesforce_product_id: matchedProduct.SalesforceProductID || matchedProduct.SalesforceProductId || matchedProduct.salesforce_product_id || '',
        // Handle: SalesforcePricebookEntryID, PricebookEntryId, pricebook_entry_id, salesforce_pricebook_entry_id
        pricebook_entry_id: matchedProduct.SalesforcePricebookEntryID || matchedProduct.PricebookEntryId || matchedProduct.pricebook_entry_id || matchedProduct.salesforce_pricebook_entry_id || '',
        currency: poData?.Currency || poData?.currency || matchedProduct.Currency || 'USD'
      };
    });

    // If no PO line items but have matched products, use those
    const finalItems = mergedItems.length > 0 ? mergedItems : matchedProducts.map(mp => ({
      line_item: {
        LineNumber: mp.LineNumber || mp.line_number || mp.po_line_index || null,
        ItemCodeSKU: mp.ItemCodeSKU || mp.item_code_sku || mp.salesforce_product_name || '',
        UnitPrice: mp.UnitPrice || mp.unit_price || mp.po_unit_price || 0,
        Quantity: mp.Quantity || mp.quantity || mp.po_quantity || 1,
        TotalAmount: mp.TotalAmount || mp.total_amount || ((mp.po_unit_price || 0) * (mp.po_quantity || 1)),
        ItemDescription: mp.ItemDescription || mp.item_description || mp.po_item_description || ''
      },
      // Handle various casing: SalesforceProductID, SalesforceProductId, salesforce_product_id
      salesforce_product_id: mp.SalesforceProductID || mp.SalesforceProductId || mp.salesforce_product_id || '',
      // Handle: SalesforcePricebookEntryID, PricebookEntryId, pricebook_entry_id, salesforce_pricebook_entry_id
      pricebook_entry_id: mp.SalesforcePricebookEntryID || mp.PricebookEntryId || mp.pricebook_entry_id || mp.salesforce_pricebook_entry_id || '',
      currency: mp.Currency || 'USD'
    }));

    console.log('📦 [PreSalesOrderCreationModalSF] finalItems:', finalItems);

    return {
      // Account info (handle both PascalCase and snake_case)
      account_id: accountData?.AccountId || accountData?.Id || accountData?.account_id || '',
      account_name: accountData?.Name || accountData?.account_name || poData?.CustomerCompanyName || poData?.customer_name || '',

      // PO info (handle both PascalCase and snake_case)
      customer_po_number: poData?.PONumber || poData?.customer_po_number || '',
      sales_date: poData?.PODate || poData?.sales_date || '',
      currency: poData?.Currency || poData?.currency || 'USD',
      total_amount: poData?.TotalAmount || poData?.total_amount || finalItems.reduce((sum, item) => sum + (item.line_item?.TotalAmount || item.line_item?.total_amount || 0), 0),

      // Billing Address (new separate fields for Salesforce)
      bill_to_street: poData?.bill_to_street || poData?.bill_to_address || '',
      bill_to_city: poData?.bill_to_city || '',
      bill_to_state: poData?.bill_to_state || '',
      bill_to_postal_code: poData?.bill_to_postal_code || '',
      bill_to_country: poData?.bill_to_country || '',

      // Shipping Address (new separate fields for Salesforce)
      ship_to_street: poData?.ship_to_street || poData?.ship_to_address || '',
      ship_to_city: poData?.ship_to_city || '',
      ship_to_state: poData?.ship_to_state || '',
      ship_to_postal_code: poData?.ship_to_postal_code || '',
      ship_to_country: poData?.ship_to_country || '',

      // Line items
      matched_items: finalItems,
      unmatched_items: mappingData?.unmatched_items || [],
      mapping_confidence: mappingData?.mapping_confidence
    };
  }, [salesOrderData, workflowContext, isWaitingForContext]);

  // Track if we've initialized selections
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize account and products from data
  useEffect(() => {
    if (!currentData || hasInitialized) return;

    // Set account
    if (currentData.account_id || currentData.salesforce_account_id) {
      setSelectedAccount(currentData.account_id || currentData.salesforce_account_id);
    }

    // Set product selections from matched items
    const matchedItems = currentData.matched_items || currentData.line_items || [];
    const productSelections = {};
    matchedItems.forEach((item, index) => {
      if (item.salesforce_product_id) {
        productSelections[index] = item.salesforce_product_id;
      }
    });
    setSelectedProducts(productSelections);

    setHasInitialized(true);
  }, [currentData, hasInitialized]);

  // Format currency
  const formatCurrency = (amount, currencySymbol = '$') => {
    if (amount === null || amount === undefined) return '-';
    return `${currencySymbol}${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle cancel - redirect based on source
  const handleCancel = () => {
    console.log('🔴 SF Modal Cancel clicked - agentId:', agentId);
    if (onCancel) onCancel();
    if (onClose) onClose();
    // If opened from agent dashboard, go back to agent; otherwise go to workflows
    if (agentId) {
      console.log('🔴 Navigating to agent:', `/agents/${agentId}`);
      navigate(`/agents/${agentId}`);
    } else {
      console.log('🔴 Navigating to workflows page');
      navigate('/workflows');
    }
  };

  // Handle load all accounts
  const handleLoadAllAccounts = async () => {
    const authToken = token || getAuthToken();
    const effectiveRunId = propRunId || workflowContext?.run_id;

    if (!authToken || !effectiveRunId) {
      console.error('Cannot load accounts: missing token or runId');
      alert('Cannot load accounts: Missing authentication or workflow ID.');
      return;
    }

    setLoadingAllAccounts(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';
      const url = `${API_BASE_URL}/api/sale-sf/salesforce-accounts-all?run_id=${effectiveRunId}`;

      console.log(`Loading all Salesforce accounts from: ${url}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      const data = await response.json();

      if (data.success && data.accounts) {
        console.log(`Loaded ${data.accounts.length} accounts`);
        setAllAccounts(data.accounts);
      } else {
        console.error('Failed to load accounts:', data.error);
        alert(`Failed to load accounts: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      alert(`Error loading accounts: ${err.message}`);
    } finally {
      setLoadingAllAccounts(false);
    }
  };

  // Handle load all products
  const handleLoadAllProducts = async () => {
    const authToken = token || getAuthToken();
    const effectiveRunId = propRunId || workflowContext?.run_id;

    if (!authToken || !effectiveRunId) {
      console.error('Cannot load products: missing token or runId');
      alert('Cannot load products: Missing authentication or workflow ID.');
      return;
    }

    setLoadingAllProducts(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';
      const url = `${API_BASE_URL}/api/sale-sf/salesforce-products-all?run_id=${effectiveRunId}`;

      console.log(`Loading all Salesforce products from: ${url}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      const data = await response.json();

      if (data.success && data.products) {
        console.log(`Loaded ${data.products.length} products`);
        setAllProducts(data.products);
      } else {
        console.error('Failed to load products:', data.error);
        alert(`Failed to load products: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      alert(`Error loading products: ${err.message}`);
    } finally {
      setLoadingAllProducts(false);
    }
  };

  // Handle create Salesforce Order
  const handleCreateOrder = async () => {
    setLoading(true);
    setCreationStatus(null);

    try {
      // Get account data for address info
      const accountStepData = workflowContext?.search_salesforce_account_ai_step_data;
      const accountInfo = accountStepData?.SelectedAccount || accountStepData || {};

      // Get PO data for addresses
      const poStepData = workflowContext?.extract_purchase_order_data_ai_step_data;
      const poInfo = poStepData?.sale_data || poStepData || {};

      // Build line items for Salesforce OrderItem
      const matchedItems = currentData?.matched_items || currentData?.line_items || [];
      const lineItems = matchedItems.map((item, index) => {
        return {
          PricebookEntryId: item.pricebook_entry_id || '',
          Quantity: item.line_item?.Quantity || item.quantity || 1,
          UnitPrice: item.line_item?.UnitPrice || item.unit_price || 0,
          Description: item.line_item?.ItemDescription || item.description || ''
        };
      });

      // Format effective date (YYYY-MM-DD)
      const effectiveDate = currentData?.sales_date
        ? new Date(currentData.sales_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      // Build order_data for Salesforce Order object
      // NOTE: PoNumber is NOT a standard field on Salesforce Order object - include in Description instead
      const poNumber = currentData?.customer_po_number || currentData?.po_number || '';

      const orderData = {
        // Required fields
        AccountId: selectedAccount || currentData?.account_id,
        EffectiveDate: effectiveDate,
        Status: 'Draft',  // Always start as Draft

        // Currency
        CurrencyIsoCode: currentData?.currency || 'USD',

        // Description includes PO reference (PoNumber is NOT a standard Salesforce Order field)
        Description: poNumber
          ? `PO#: ${poNumber} - Sales Order for ${currentData?.account_name || 'Customer'}`
          : `Sales Order for ${currentData?.account_name || 'Customer'}`,

        // Billing Address (from PO extraction - new separate fields)
        BillingStreet: poInfo?.bill_to_street || poInfo?.bill_to_address || accountInfo?.BillingStreet || '',
        BillingCity: poInfo?.bill_to_city || accountInfo?.BillingCity || '',
        BillingState: poInfo?.bill_to_state || accountInfo?.BillingState || '',
        BillingPostalCode: poInfo?.bill_to_postal_code || accountInfo?.BillingPostalCode || '',
        BillingCountry: poInfo?.bill_to_country || accountInfo?.BillingCountry || '',

        // Shipping Address (from PO extraction - new separate fields)
        ShippingStreet: poInfo?.ship_to_street || poInfo?.ship_to_address || accountInfo?.ShippingStreet || '',
        ShippingCity: poInfo?.ship_to_city || accountInfo?.ShippingCity || '',
        ShippingState: poInfo?.ship_to_state || accountInfo?.ShippingState || '',
        ShippingPostalCode: poInfo?.ship_to_postal_code || accountInfo?.ShippingPostalCode || '',
        ShippingCountry: poInfo?.ship_to_country || accountInfo?.ShippingCountry || ''
      };

      // Build the complete payload matching backend expectations
      const orderPayload = {
        order_data: orderData,
        line_items: lineItems,
        activate_order: false  // Keep as Draft for review
      };

      console.log('📦 Sending Salesforce Order payload:', orderPayload);
      console.log('📦 order_data:', orderData);
      console.log('📦 line_items:', lineItems);

      // Call parent's onConfirm handler
      if (onConfirm) {
        onConfirm(orderPayload);

        setCreationStatus('processing');
        setCreationMessage('Salesforce Order creation in progress... Check the chat for updates.');

        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      } else {
        setCreationStatus('error');
        setCreationMessage('Unable to submit - confirmation handler not available');
      }
    } catch (err) {
      console.error('Error creating Salesforce Order:', err);
      setCreationStatus('error');
      setCreationMessage(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // If no data or waiting for workflow context, show loading
  if (!currentData || isWaitingForContext) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">
            {isWaitingForContext
              ? 'Fetching order data from Salesforce workflow...'
              : 'Loading sales order data...'}
          </p>
          <p className="mt-2 text-gray-400 text-sm">
            Run ID: {propRunId?.slice(0, 8)}...
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (creationStatus === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Order Created!</h3>
          <p className="text-gray-600 mb-6">{creationMessage}</p>
          {salesforceUrl && (
            <a
              href={salesforceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition mb-4"
            >
              View in Salesforce
            </a>
          )}
          <button onClick={onClose} className="block w-full mt-4 text-gray-500 hover:text-gray-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  // Processing state
  if (creationStatus === 'processing') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Creating Order...</h3>
          <p className="text-gray-600 mb-4">{creationMessage}</p>
          <p className="text-sm text-gray-500">This window will close automatically.</p>
        </div>
      </div>
    );
  }

  // Get line items
  const lineItems = currentData.matched_items || currentData.line_items || [];
  const currencySymbol = currentData.currency || '$';

  return (
    <div className={`fixed z-50 transition-all duration-300 ${
      isMinimized
        ? 'inset-auto bottom-0 right-0 p-0'
        : 'inset-0 bg-black/50 flex items-center justify-center p-4'
    }`}>
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl transition-all duration-300 ${
        isMinimized
          ? 'fixed bottom-4 right-4 w-80 max-w-[90vw]'
          : 'w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col'
      }`}>
        {/* Header - Salesforce Blue themed */}
        <div className={`bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-3 flex items-center justify-between ${isMinimized ? 'rounded-t-xl' : ''}`}>
          <div className="flex items-center gap-3">
            {!isMinimized && (
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
            )}
            <div>
              <h2 className={`font-bold text-white ${isMinimized ? 'text-sm' : 'text-xl'}`}>
                {isMinimized ? '☁️ Salesforce Review' : 'Review Salesforce Order'}
              </h2>
              {!isMinimized && (
                <p className="text-white/80 text-sm">Confirm details before creating in Salesforce</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-white/20 rounded-lg transition"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMinimized ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                )}
              </svg>
            </button>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-white/20 rounded-lg transition"
              title="Close"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Minimized status indicator */}
        {isMinimized && (
          <div className="p-3 bg-white dark:bg-gray-800 rounded-b-xl">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Click maximize to review order details
            </p>
          </div>
        )}

        {!isMinimized && (
          <>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Error message */}
              {creationStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-red-800">Creation Failed</p>
                    <p className="text-red-600 text-sm">{creationMessage}</p>
                  </div>
                </div>
              )}

              {/* Account and Order Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Account Info */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Salesforce Account
                    {!currentData.account_id && !selectedAccount && (
                      <span className="ml-2 text-yellow-600 text-sm">⚠️ Not matched</span>
                    )}
                  </h3>

                  {currentData.account_id && selectedAccount ? (
                    <div className="space-y-2">
                      <p className="text-lg font-medium text-gray-900">{currentData.account_name || 'N/A'}</p>
                      <p className="text-sm text-gray-500">ID: {currentData.account_id}</p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {currentData.account_name ? `Account "${currentData.account_name}" not found - Select:` : 'Select Account:'}
                      </label>

                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          placeholder="Search accounts..."
                          value={accountSearchTerm}
                          onChange={(e) => setAccountSearchTerm(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-white text-gray-900"
                          disabled={!allAccounts.length}
                        />
                        <button
                          onClick={handleLoadAllAccounts}
                          disabled={loadingAllAccounts || allAccounts.length > 0}
                          className={`px-4 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                            allAccounts.length > 0
                              ? 'bg-green-600 text-white cursor-not-allowed'
                              : loadingAllAccounts
                              ? 'bg-gray-400 text-white cursor-wait'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {loadingAllAccounts ? '⏳ Loading...' : allAccounts.length > 0 ? '✅ Loaded' : '🏢 Load All'}
                        </button>
                      </div>

                      <select
                        value={selectedAccount}
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
                      >
                        <option value="">
                          {allAccounts.length > 0
                            ? `-- Select from ${allAccounts.length} accounts --`
                            : '-- Click "Load All" to load accounts --'}
                        </option>
                        {allAccounts
                          .filter(acc => {
                            if (!accountSearchTerm) return true;
                            const name = (acc.Name || acc.name || '').toLowerCase();
                            return name.includes(accountSearchTerm.toLowerCase());
                          })
                          .map(acc => (
                            <option key={acc.Id || acc.id} value={acc.Id || acc.id}>
                              {acc.Name || acc.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Order Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Order Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">PO Number:</span>
                      <p className="font-medium">{currentData.customer_po_number || currentData.po_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Order Date:</span>
                      <p className="font-medium">{currentData.sales_date || currentData.po_date || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Currency:</span>
                      <p className="font-medium">{currentData.currency || 'INR'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Total:</span>
                      <p className="font-medium text-lg">{formatCurrency(currentData.total_amount, currencySymbol)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing & Shipping Addresses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Billing Address */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Bill To Address
                  </h3>
                  <div className="text-sm space-y-1">
                    <p className="font-medium">{currentData.bill_to_street || 'N/A'}</p>
                    <p className="text-gray-600">
                      {[currentData.bill_to_city, currentData.bill_to_state].filter(Boolean).join(', ') || ''}
                    </p>
                    <p className="text-gray-600">
                      {[currentData.bill_to_postal_code, currentData.bill_to_country].filter(Boolean).join(' ') || ''}
                    </p>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
                    </svg>
                    Ship To Address
                  </h3>
                  <div className="text-sm space-y-1">
                    <p className="font-medium">{currentData.ship_to_street || 'N/A'}</p>
                    <p className="text-gray-600">
                      {[currentData.ship_to_city, currentData.ship_to_state].filter(Boolean).join(', ') || ''}
                    </p>
                    <p className="text-gray-600">
                      {[currentData.ship_to_postal_code, currentData.ship_to_country].filter(Boolean).join(' ') || ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Line Items ({lineItems.length})
                  </h3>
                  <button
                    type="button"
                    onClick={handleLoadAllProducts}
                    disabled={loadingAllProducts || allProducts.length > 0}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition flex items-center gap-1.5 ${
                      allProducts.length > 0
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : loadingAllProducts
                        ? 'bg-gray-100 text-gray-500 cursor-wait'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {loadingAllProducts ? (
                      <>⏳ Loading...</>
                    ) : allProducts.length > 0 ? (
                      <>✅ {allProducts.length} Products Loaded</>
                    ) : (
                      <>📦 Load All Products</>
                    )}
                  </button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Qty</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Unit Price</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Salesforce Product</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {lineItems.map((item, index) => {
                        const lineItem = item.line_item || item;
                        // Use original line number from PO if available, otherwise use index + 1
                        const displayLineNumber = lineItem.LineNumber || lineItem.line_number || (index + 1);
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500">{displayLineNumber}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {lineItem.ItemDescription || item.description || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-center text-gray-900">
                              {lineItem.Quantity || item.quantity || 0}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {formatCurrency(lineItem.UnitPrice || item.unit_price, currencySymbol)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                              {formatCurrency(lineItem.TotalAmount || item.amount, currencySymbol)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {allProducts.length > 0 ? (
                                <div className="relative">
                                  <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={productSearchTerms[index] || ''}
                                    onChange={(e) => setProductSearchTerms(prev => ({ ...prev, [index]: e.target.value }))}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                  />
                                  <select
                                    value={String(selectedProducts[index] || item.salesforce_product_id || '')}
                                    onChange={(e) => setSelectedProducts(prev => ({ ...prev, [index]: e.target.value }))}
                                    className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                                  >
                                    <option value="">-- Select Product --</option>
                                    {allProducts
                                      .filter(p => {
                                        const searchTerm = (productSearchTerms[index] || '').toLowerCase();
                                        if (!searchTerm) return true;
                                        const name = (p.Name || p.name || '').toLowerCase();
                                        const code = (p.ProductCode || p.product_code || '').toLowerCase();
                                        return name.includes(searchTerm) || code.includes(searchTerm);
                                      })
                                      .map(p => (
                                        <option key={p.Id || p.id} value={p.Id || p.id}>
                                          {p.ProductCode || p.product_code} - {p.Name || p.name}
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              ) : item.salesforce_product_id ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  {lineItem.ItemCodeSKU || `ID: ${item.salesforce_product_id.slice(0, 10)}...`}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                                  ⚠️ Not matched
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-right font-semibold text-gray-900">
                          Total:
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-lg text-gray-900">
                          {formatCurrency(currentData.total_amount, currencySymbol)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Mapping Confidence */}
              {currentData.mapping_confidence && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium text-green-800">
                      Product Mapping Confidence: {(currentData.mapping_confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrder}
                disabled={loading || (!selectedAccount && !currentData.account_id)}
                className="px-8 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-700 transition disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                    Create in Salesforce
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PreSalesOrderCreationModalSF;
